// "Go to the next unsaved change" (TGCLOUD-1902, rule 5): a form's count of
// unsaved changes, clicked, moves focus to the next changed field, in screen
// order, and round again from the first. Found in the DOM, where ChangedField
// marks each field it draws as changed, so a form needs no list of refs:
// whatever is gold is where the count takes you, inside a collapsed section too.

/** A field drawn as changed, and not inside a part of the form with a Save of its own. */
export const CHANGED_SELECTOR =
  '[data-changed="true"]:not([data-changed-scope])';

// What a clinician types into or chooses with, inside a changed field.
const FOCUSABLE = [
  'input:not([type="hidden"]):not([disabled]):not([aria-hidden="true"])',
  'textarea:not([disabled]):not([aria-hidden="true"])',
  'select:not([disabled])',
  '[role="combobox"]:not([aria-disabled="true"])',
  '[contenteditable="true"]',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

/** Every changed field under `root`, in screen (document) order. */
export const changedElementsIn = (root) =>
  root ? Array.from(root.querySelectorAll(CHANGED_SELECTOR)) : [];

/**
 * The changed field after `after` in screen order, or the first one when
 * `after` is gone, is the last, or was never set. Null when nothing is changed.
 */
export function nextChangedElement(root, after) {
  const all = changedElementsIn(root);
  if (all.length === 0) return null;
  if (!after || !after.isConnected) return all[0];
  const next = all.find(
    (el) =>
      el !== after &&
      // eslint-disable-next-line no-bitwise
      after.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
  );
  return next || all[0];
}

/**
 * Scroll a changed field into view and focus its input (which shows its "Was:"
 * tooltip). A field whose input is locked (a date of death cleared by Alive) is
 * focused as a whole, so the jump still lands on it.
 */
export function focusChangedElement(el) {
  if (!el) return;
  el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  const target = el.querySelector(FOCUSABLE);
  if (target) {
    target.focus({ preventScroll: true });
    return;
  }
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
}
